import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2, WritableSignal, computed, inject, signal } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { FooterComponent } from "../shared/footer/footer.component";
import { Router, RouterLink } from "@angular/router";
import { ResolutionService } from '../shared/resolution.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { InstagramService } from '../shared/instagram.service';
import { InstagramPost } from '../shared/types/common.types';
import { InstagramMediaType } from '../shared/types/common.enums';
import KeenSlider from 'keen-slider';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { LocalStorageService } from '../shared/local-storage.service';
import { MenuService } from '../shared/menu.service';
import { formatStringForURL } from '../shared/common/utils';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { PostComponent } from '../pages/blog/post/post.component';
import { BlogService } from '../pages/blog/blog.service';
import { BlogPost } from '../pages/blog/blog.types';
import { finalize, tap } from 'rxjs';
import { CanonicalService } from '../shared/canonical.service';

@Component({
  selector: 'app-home',
  imports: [
    FooterComponent,
    RouterLink,
    PostComponent
],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  host: { 'class': 'home-page' }
})
export class HomeComponent implements OnInit, OnDestroy {

  private meta = inject(Meta);
  private title = inject(Title);
  #document = inject(DOCUMENT);
  #canonicalService = inject(CanonicalService);
  #localBusinessScript?: HTMLScriptElement;

  private resolutionService = inject(ResolutionService);
  private signalsStoreService = inject(SignalsStoreService);
  #router = inject(Router);
  #menuService = inject(MenuService);
  #blogService = inject(BlogService);
  #instagramService = inject(InstagramService);
  #localStorageService = inject(LocalStorageService);

  hasSession = computed(() => this.signalsStoreService.hasSession());

  blogs = signal<BlogPost[]>([]);

  featureProducts = [
    {
      img: 'assets/images/landing/home/feature-products/1.webp',
      title: 'Farm Boxes',
      category: 'FarmBoxes'
    },
    {
      img: 'assets/images/landing/home/feature-products/2.webp',
      title: 'Dairy',
      category: 'Dairy & Eggs'
    },
    {
      img: 'assets/images/landing/home/feature-products/3.webp',
      title: 'Produce',
      category: 'Produce'
    },
    {
      img: 'assets/images/landing/home/feature-products/4.webp',
      title: 'Bakery',
      category: 'Bakery'
    },
    {
      img: 'assets/images/landing/home/feature-products/5.webp',
      title: 'Drinks',
      category: 'Drinks'
    },
    {
      img: 'assets/images/landing/home/feature-products/6.webp',
      title: 'Meat & Fish',
      category: 'Meats'
    },
    {
      img: 'assets/images/landing/home/feature-products/7.webp',
      title: 'New Arrivals',
      category: 'New Arrivals'
    },
    {
      img: 'assets/images/landing/home/feature-products/8.webp',
      title: 'Pantry',
      category: 'Pantry'
    }
  ];
  testimonials = [
    {
      count: 1,
      quote: 'We’ve been getting our vegetables from here for the last few months and I could not be more pleased. The quality has been amazing and it really helps keep our dinners nice and healthy at home. Fantastic service!',
      name: 'Bobby E',
    },
    {
      count: 2,
      quote: 'We just received our first FarmBox delivery and couldn’t be happier. The quality of the fruits and veggies is amazing! We love knowing that we are supporting local farmers and eating nutritious foods. Five stars!!',
      name: 'Shannon Duke Fan',
    },
    {
      count: 3,
      quote: 'Lovely service! The produce is amazing, their ever growing website has so many new products, and their customer service is always pleasant and eager to help! Definitely recommend!',
      name: '•Sacred Woman•',
    }, {
      count: 4,
      quote: 'Great food, easy to customize, and so simple to change delivery when I\'m out of town! I\'ve tried others but this one is the best!',
      name: 'Dana Phillips',
    },
    {
      count: 5,
      quote: 'I love my weekly farm box delivery! I get a weekly produce box and it\'s been so good. The fruits and vegetables are fresh and delicious. I have also ordered eggs, milk, cream, and beef liver. Everything comes packaged well and they take the old boxes and packaging to be reused. Their customer service is great! They are responsive and polite. I LOVE FARM BOX DELIVERY',
      name: 'Kayla Fudge',
    },
    {
      count: 6,
      quote: 'Loving the farm box experience! Everything has been fresh and ripe and I love that it\'s delivered. I think the price is great. I\'ve paid just as much for only organic berries and now I get a week off organic produce for my family delivered. Excellent customer service, super nice. I highly recommend if you are in DFW.',
      name: 'Sara Hanshew',
    },
  ];
  testimonials2 = [
    {
      count: 1,
      quote: 'Its really nice to get farm fresh food without having to worry about crowds at farmers markets.',
      name: 'Jacqui D.',
    }, {
      count: 2,
      quote: 'A big factor for me is less waste. I like that Farmhouse is local, supports local farmers and picks up the packaging when they deliver the next week',
      name: 'Sarah C.',
    },
    {
      count: 3,
      quote: 'My Farmhouse Delivery box is the best part of my week. My palate feels expanded and horizons broadened, it’s like getting presents.',
      name: 'Margaret F.',
    },
    {
      count: 4,
      quote: 'A big factor for me is less waste. I like that Farmhouse is local, supports local farmers and picks up the packaging when they deliver the next week',
      name: 'Sarah C.',
    },
    {
      count: 5,
      quote: 'My Farmhouse Delivery box is the best part of my week. My palate feels expanded and horizons broadened, it’s like getting presents.',
      name: 'Margaret F.',
    },
  ];

  sections: HTMLElement[] = [];

  videoUrl: WritableSignal<string> = signal('');

  isMobile = computed(() => this.resolutionService.isMobile())

  instagramPost = signal<InstagramPost[]>([]);

  IGImage = InstagramMediaType.IMAGE;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (!isPlatformBrowser(this.platformId)) return;
    this.#getInstagramMedia();
  }

  ngOnInit() {
    this.setUpSEOTags();
    // Retrieve all sections
    const sectionElements = this.elementRef.nativeElement.querySelectorAll('.has-animation');
    this.sections = Array.from(sectionElements);

    //WordPress post
    if (isPlatformBrowser(this.platformId))
      this.getLatestPosts();
  }

  getLatestPosts(): void {

    this.#blogService.page.set(0);
    this.#blogService.size.set(3);

    this.#blogService
      .getAll()
      .pipe(
        finalize(() => {
          this.#blogService.page.set(0);
          this.#blogService.size.set(10);
        }),
        tap(response => {
          this.blogs.set(response.data?.blogs ?? []);
        })
      )
      .subscribe();
  }

  private setUpSEOTags() {

    const { mainTitle, mainDescription, baseKeywords: keywords, baseURL } = environment.config.seo;

    // Title + Open Graph + Twitter handled via CanonicalService (updateTag, so the
    // static defaults in index.html are overwritten rather than duplicated).
    this.#canonicalService.setPageTitle(mainTitle);
    this.#canonicalService.setPageUrl(baseURL);
    this.#canonicalService.setPageDescription(mainDescription);
    this.#canonicalService.setPageImage(`${baseURL}assets/images/landing/home/bg-hero.webp`);
    this.#canonicalService.setPageType('website');

    this.meta.updateTag({ name: 'keywords', content: keywords.join(', ') });

    this.#setLocalBusinessJsonLd();
  }

  #setLocalBusinessJsonLd() {
    if (this.#localBusinessScript) {
      this.renderer.removeChild(this.#document.head, this.#localBusinessScript);
      this.#localBusinessScript = undefined;
    }

    const lb = environment.config.seo.localBusiness;

    // Remove the SSR-rendered copy (the client has no reference to it). Driven by
    // the configured type plus the historical ones, rather than a hardcoded list:
    // this used to name GroceryStore literally, so changing `localBusiness.type`
    // silently stopped matching and left the SSR block in place alongside the
    // client's — two business blocks on the page.
    const businessTypes = new Set([lb.type, 'LocalBusiness', 'FoodEstablishment', 'GroceryStore', 'OnlineStore', 'Organization']);
    const ssrScripts = this.#document.head.querySelectorAll('script[type="application/ld+json"]');
    ssrScripts.forEach(s => {
      let emittedType: string | null = null;
      try { emittedType = JSON.parse(s.textContent || '')['@type']; } catch { /* not ours */ }
      if (emittedType && businessTypes.has(emittedType)) {
        this.renderer.removeChild(this.#document.head, s);
      }
    });

    const data: any = {
      '@context': 'https://schema.org',
      '@type': lb.type,
      name: environment.application.name,
      url: environment.config.seo.baseURL,
      description: environment.config.seo.mainDescription,
      telephone: lb.telephone,
      email: lb.email,
      image: lb.image,
      contactPoint: {
        '@type': 'ContactPoint',
        email: environment.config.contactEmail,
        contactType: 'customer service'
      }
    };
    // Only emit `address` when there is a real one to emit. Spreading an absent
    // `lb.address` would publish an empty PostalAddress, which is worse than
    // omitting the property. Farmbox is delivery-only; `areaServed` carries the
    // local signal. Read through an optional type so dropping the key from the
    // environment is legal here, and adding it back starts working immediately.
    const address = (lb as { address?: Record<string, string> }).address;
    if (address) data.address = { '@type': 'PostalAddress', ...address };
    if (lb.sameAs?.length) data.sameAs = lb.sameAs;
    if (lb.areaServed?.length) data.areaServed = lb.areaServed.map(a => ({ '@type': 'City', name: a.name }));
    // priceRange / paymentAccepted / currenciesAccepted are deliberately not emitted:
    // schema.org scopes all three to LocalBusiness, and this is an OnlineStore
    // (Organization branch). They only ever fed the local rich result, which a
    // service-area business without a published address cannot earn anyway.

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    this.renderer.appendChild(this.#document.head, script);
    this.#localBusinessScript = script;
  }

  ngOnDestroy(): void {
    if (this.#localBusinessScript) {
      this.renderer.removeChild(this.#document.head, this.#localBusinessScript);
    }
  }

  @HostListener('scroll', ['$event'])
  onElementScroll(event: any) {
    const scrollPosition = event.target.scrollTop;
    const windowHeight = event.target.clientHeight;

    // Loop through each section
    this.sections.forEach(section => {
      const sectionOffset = section.offsetTop;
      const sectionHeight = section.offsetHeight;

      // Check if the section is in the viewport
      if (scrollPosition >= sectionOffset - windowHeight && scrollPosition < sectionOffset + sectionHeight) {
        // Add a class to the section
        this.renderer.addClass(section, 'animated');
      } else {
        // Remove the class from the section
        this.renderer.removeClass(section, 'animated');
      }
    });
  }

  referFriend() {
    if (this.signalsStoreService.hasSession())
      this.#router.navigate(['/settings/account/friend-referrals']);
    else
      this.#router.navigate(['/signup']);
  }

  async #getInstagramMedia() {

    this.instagramPost.set([]);

    // this.#instagramService
    //   .getAllMedia(12)
    //   .pipe(
    //     tap(result => {

    //       if (!result || !result.data || !result.data.length)
    //         return;

    //       this.instagramPost.set(result.data);

    //       setTimeout(() => {
    //         this.instagramPost()
    //           .filter(x => x.media_type === InstagramMediaType.CAROUSEL_ALBUM)
    //           .forEach(x => {
    //             this.#initInstagramSlider(x);
    //           });
    //       }, 1000);
    //     })
    //   )
    //   .subscribe();
  }

  #initInstagramSlider(post: InstagramPost) {

    post.currentSlider = 0;
    const sliderElement = this.elementRef.nativeElement.querySelector(`#instagram-slider-${post.id}`);

    post.slider = new KeenSlider(sliderElement, {
      initial: post.currentSlider,
      loop: true,
      slideChanged: s => post.currentSlider = s.track.details.rel
    });

    if (post.slider.track?.details?.slides?.length) {
      post.dotHelper = [
        ...Array(post.slider.track.details.slides.length)
      ];
    }
  }

  goToShop(categoryName: string) {

    const category = this.#menuService
      .menu()
      .find(x => x.name.toLowerCase() === categoryName.toLowerCase());

    if (!category)
      return;

    const formattedCategoryName = formatStringForURL(category.name);
    this.signalsStoreService.selectedCategory.set(category);
    this.#localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_CATEGORY, formattedCategoryName);
    this.#router.navigate([`/shop/${formattedCategoryName}`]);
  }
}



